import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Lock, Warning, Check, Minus, Plus } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { getTrip, patchStrokes, patchRound } from '../lib/api';
import { getGroupClaimData } from '../lib/ownerAuth';
import { getTripAccess } from '../lib/tripAccess';
import { isRoundFrozen, stablefordPoints, shotsOnHole, getEffectivePH, getAllowedPH, effectiveHolePars, s36Handicap, effectivePHForBasis, formatPlayingHandicap } from '../golf';
import { effectiveCourseForPlayer, effectiveHoleParsForPlayer, effectiveStrokeIndexesForPlayer } from '../lib/effectiveCourse';
import { roundFormat } from '../lib/formatHelpers';
import { computeTeamPH } from '../lib/scramble';
import { toast } from 'sonner';

/* ── Score colour helper — identical to ScorecardView.jsx:29-38 ── */
function scoreColorClass(strokes, par) {
  if (!strokes || strokes <= 0) return 'score-unscored';
  const diff = strokes - par;
  if (diff <= -2) return 'score-eagle';
  if (diff === -1) return 'score-birdie';
  if (diff === 0) return 'score-par';
  if (diff === 1) return 'score-bogey';
  if (diff === 2) return 'score-double-bogey';
  return 'score-triple-plus';
}

/* Grid column template: [Label] [9 hole cols] [Out/In] [Tot] = 12 cols */
const GRID_COLS = 'grid-cols-[minmax(32px,40px)_repeat(9,minmax(22px,1fr))_minmax(26px,38px)_minmax(26px,38px)]';

export default function ReviewScorecard() {
  const { code, roundId, groupIdx: groupIdxParam } = useParams();
  const navigate = useNavigate();
  const myGroupIdx = parseInt(groupIdxParam, 10);

  const [trip, setTrip] = useState(null);
  const [round, setRound] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locking, setLocking] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [editVal, setEditVal] = useState('');
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await getTrip(code);
      setTrip(data);
      const rd = data.rounds?.find(r => r.id === roundId);
      setRound(rd || null);
    } catch {}
    setLoading(false);
  }, [code, roundId]);

  useEffect(() => { load(); }, [load]);

  // ── Freeze / claim guards ──
  const isFrozen = round ? isRoundFrozen(round) : false;
  const freezeRestricted = isFrozen && !round?.revealed;
  const claimData = freezeRestricted ? getGroupClaimData(code, roundId) : null;
  const access = getTripAccess(code);
  const isOrganiser = access?.role === 'organiser' || !!access?.legacy;
  const hasOwnGroup = claimData?.group_idx != null && claimData.group_idx >= 0;
  const showNoClaim = freezeRestricted && !isOrganiser && !hasOwnGroup;

  const fmt = round ? roundFormat(round) : 'stableford';
  const isScramble = round?.scoring_mode === 'texas_scramble';
  const teams = round?.teams || [];
  const players = trip?.players || [];
  const myGroupPids = teams[myGroupIdx] || [];
  const myGroupPlayers = myGroupPids.map(pid => players.find(p => p.id === pid)).filter(Boolean);
  const holePars = round?.course?.hole_pars || Array(18).fill(4);

  // ── Unlocked-draw detection: structural data missing ──
  const drawReady = round && teams.length > 0 && myGroupPids.length > 0 && (isScramble || myGroupPlayers.length > 0);

  // ── Player helpers ──
  const getPlayerPH = (player) => {
    if (!round) return 0;
    const course = effectiveCourseForPlayer(round, player.id);
    const enteredPH = getAllowedPH(player, course, round);
    const basis = round.handicap_basis;
    const isS36B = basis === 'system36' || basis === 'lower_of';
    if (!isS36B) return enteredPH;
    const strokes = round.hole_strokes?.[player.id] ?? [];
    const pars = effectiveHolePars(round);
    const s36H = s36Handicap(strokes, pars, round.s36_max_handicap ?? null);
    const { ph } = effectivePHForBasis(basis, enteredPH, s36H);
    return ph ?? 0;
  };
  const getPlayerHolePars = (pid) => round ? effectiveHoleParsForPlayer(round, pid) : holePars;
  const getPlayerSIs = (pid) => round ? effectiveStrokeIndexesForPlayer(round, pid) : Array.from({length:18},(_,i)=>i+1);
  const getStrokes = (pid, hole) => round?.hole_strokes?.[pid]?.[hole] || 0;
  const getTeamStrokes = (hole) => round?.team_hole_strokes?.[String(myGroupIdx)]?.[hole] || 0;
  const isNR = (pid) => (round?.nr_players || []).includes(pid);
  const isHoleNR = (pid, hole) => (round?.hole_nr?.[pid] || []).includes(hole);

  // ── Missing holes ──
  const missingHoles = (() => {
    if (!round || !drawReady) return [];
    const missing = new Set();
    if (isScramble) {
      for (let h = 0; h < 18; h++) if (getTeamStrokes(h) <= 0) missing.add(h);
    } else {
      for (const pid of myGroupPids) {
        if (isNR(pid)) continue;
        for (let h = 0; h < 18; h++) {
          if (isHoleNR(pid, h)) continue;
          if (getStrokes(pid, h) <= 0) missing.add(h);
        }
      }
    }
    return Array.from(missing).sort((a, b) => a - b);
  })();

  // ── Inline edit ──
  const startEdit = (playerId, hole) => {
    const val = isScramble ? getTeamStrokes(hole) : getStrokes(playerId, hole);
    setEditingCell({ playerId, hole });
    setEditVal(val > 0 ? String(val) : '');
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const commitEdit = async () => {
    if (!editingCell) return;
    const { playerId, hole } = editingCell;
    const v = parseInt(editVal, 10);
    setEditingCell(null);
    if (isNaN(v) || v < 0 || v > 15) return;
    const current = isScramble ? getTeamStrokes(hole) : getStrokes(playerId, hole);
    if (v === current) return;
    try {
      if (isScramble) {
        await patchStrokes(code, roundId, { playerId: myGroupPids[0] || '', hole, value: v, team_index: myGroupIdx });
      } else {
        const player = players.find(p => p.id === playerId);
        const playerPars = getPlayerHolePars(playerId);
        const playerSIs = getPlayerSIs(playerId);
        const ph = player ? getPlayerPH(player) : 0;
        const stab = stablefordPoints(v > 0 ? v : null, playerPars[hole] || 4, ph, playerSIs[hole] || 1);
        await patchStrokes(code, roundId, { playerId, hole, value: v, stableford: Math.max(0, stab ?? 0) });
      }
      await load();
    } catch { toast.error('Failed to save'); }
  };

  const adjustEdit = (delta) => {
    const cur = parseInt(editVal, 10) || 0;
    setEditVal(String(Math.max(0, Math.min(15, cur + delta))));
  };

  const markNR = async (playerId, hole) => {
    try {
      const playerPar = getPlayerHolePars(playerId)[hole] || 4;
      const ph = getPlayerPH(players.find(p => p.id === playerId));
      const playerSI = getPlayerSIs(playerId)[hole] || 1;
      const ndb = playerPar + shotsOnHole(ph, playerSI);
      await patchStrokes(code, roundId, { playerId, hole, value: ndb, nr: true });
      await load();
    } catch { toast.error('Failed to mark NR'); }
    setEditingCell(null);
  };

  // ── Lock (same payload as Scoring.jsx doFinishRound) ──
  const doLock = async () => {
    setLocking(true);
    try {
      const gf = [...(round.group_finished || Array(teams.length).fill(false))];
      while (gf.length <= myGroupIdx) gf.push(false);
      gf[myGroupIdx] = true;
      await patchRound(code, roundId, { group_finished: gf, nr_players: round.nr_players || [] });
      toast.success('Scores locked');
      navigate(`/trip/${code}/round/${roundId}/setup`);
    } catch { toast.error('Failed to lock scores'); }
    setLocking(false);
  };

  // ── Row computation ──
  const computePlayerRow = (pid) => {
    const hp = getPlayerHolePars(pid);
    const si = getPlayerSIs(pid);
    const player = players.find(p => p.id === pid);
    const ph = getPlayerPH(player);
    const out = { gross: 0, pts: 0, net: 0 };
    const inn = { gross: 0, pts: 0, net: 0 };
    const holes = [];
    for (let h = 0; h < 18; h++) {
      const s = getStrokes(pid, h);
      const par = hp[h] || 4;
      const siH = si[h] || 1;
      const hNR = isHoleNR(pid, h);
      const pts = hNR ? 0 : (s > 0 ? Math.max(0, stablefordPoints(s, par, ph, siH) ?? 0) : 0);
      const net = hNR ? 0 : (s > 0 ? s - shotsOnHole(ph, siH) : 0);
      const gross = hNR ? 0 : s;
      holes.push({ gross, pts, net, par });
      const bucket = h < 9 ? out : inn;
      bucket.gross += gross;
      bucket.pts += pts;
      bucket.net += net;
    }
    return { holes, out, inn, total: { gross: out.gross + inn.gross, pts: out.pts + inn.pts, net: out.net + inn.net } };
  };

  const computeTeamRow = () => {
    const out = { gross: 0 };
    const inn = { gross: 0 };
    const holes = [];
    for (let h = 0; h < 18; h++) {
      const s = getTeamStrokes(h);
      const par = holePars[h] || 4;
      holes.push({ gross: s, par });
      (h < 9 ? out : inn).gross += s;
    }
    return { holes, out, inn, total: { gross: out.gross + inn.gross } };
  };

  // ── Loading / Error states ──
  if (loading) return <div className="min-h-screen bg-[#F4F6F5] flex items-center justify-center"><div className="w-8 h-8 border-3 border-[#1B4332] border-t-transparent rounded-full animate-spin" /></div>;
  if (!round) return <div className="min-h-screen bg-[#F4F6F5] flex items-center justify-center"><p className="text-sm text-[#455561]">Round not found</p></div>;

  if (showNoClaim) {
    return (
      <div className="min-h-screen bg-[#F4F6F5] flex flex-col items-center justify-center px-6" data-testid="review-no-claim">
        <Lock className="w-10 h-10 text-[#B8C7C0] mb-3" weight="bold" />
        <p className="text-[#455561] text-sm text-center">Pick your group to review scores</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate(-1)} data-testid="review-back-btn">Back</Button>
      </div>
    );
  }

  // ── Unlocked draw guard ──
  if (!drawReady) {
    return (
      <div className="min-h-screen bg-[#F4F6F5]" data-testid="review-draw-unlocked">
        <div className="sticky top-0 z-20 bg-[#1B4332] text-white px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(`/trip/${code}/round/${roundId}/scoring`)} className="p-1" data-testid="review-back-scoring">
            <ArrowLeft className="w-5 h-5" weight="bold" />
          </button>
          <h1 className="text-base font-bold">Review Scorecard</h1>
        </div>
        <div className="flex flex-col items-center justify-center px-6 py-20">
          <Warning className="w-10 h-10 text-[#F57F17] mb-3" weight="bold" />
          <p className="text-[#455561] text-sm text-center max-w-xs">Scores can't be reviewed until the draw is locked. Ask your organiser.</p>
          <Button variant="outline" className="mt-4"
            onClick={() => navigate(`/trip/${code}/round/${roundId}/scoring`)}
            data-testid="review-back-btn">Back to scoring</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F6F5]" data-testid="review-scorecard">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-[#1B4332] text-white px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(`/trip/${code}/round/${roundId}/scoring`)} className="p-1" data-testid="review-back-scoring">
          <ArrowLeft className="w-5 h-5" weight="bold" />
        </button>
        <h1 className="text-base font-bold flex-1 truncate">Review Scorecard</h1>
      </div>

      <div className="px-2 py-3 max-w-2xl mx-auto space-y-3">
        {/* Missing holes banner */}
        {missingHoles.length > 0 && (
          <Card className="p-3 bg-[#FFF8E1] border-[#FFD54F] rounded-xl flex items-start gap-2" data-testid="review-missing-banner">
            <Warning className="w-5 h-5 text-[#F57F17] flex-shrink-0 mt-0.5" weight="bold" />
            <p className="text-xs text-[#795548]">
              <span className="font-semibold">Holes missing a score: </span>
              {missingHoles.map(h => h + 1).join(', ')}
            </p>
          </Card>
        )}

        {/* Scorecard */}
        <div className="rounded-xl border border-[#E1E6E4] bg-white overflow-hidden">
          {isScramble ? <ScrambleGrid row={computeTeamRow()} /> : myGroupPlayers.map((player, i) => (
            <PlayerGrid key={player.id} player={player} row={computePlayerRow(player.id)} isFirst={i === 0} />
          ))}
        </div>

        {/* Buttons */}
        <div className="space-y-2 pb-6">
          <Button onClick={doLock} disabled={missingHoles.length > 0 || locking}
            className="w-full h-12 rounded-xl bg-[#1B4332] text-white font-semibold disabled:opacity-40"
            data-testid="review-confirm-btn">
            {locking ? 'Locking…' : <><Check className="w-5 h-5 mr-2" weight="bold" />Confirm and lock scores</>}
          </Button>
          <Button variant="outline" onClick={() => navigate(`/trip/${code}/round/${roundId}/scoring`)}
            className="w-full h-10 rounded-xl border-[#E1E6E4] text-[#455561] text-sm"
            data-testid="review-back-btn">Back to scoring</Button>
        </div>
      </div>

      {/* Edit overlay */}
      {editingCell && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end justify-center" onClick={() => commitEdit()} data-testid="review-edit-overlay">
          <div className="w-full max-w-md bg-white rounded-t-2xl p-5 space-y-3" onClick={e => e.stopPropagation()}>
            <p className="text-sm font-semibold text-[#1D242B] text-center">
              Hole {editingCell.hole + 1} — {isScramble ? 'Team' : (players.find(p => p.id === editingCell.playerId)?.name || 'Player')}
            </p>
            <div className="flex items-center justify-center gap-4">
              <button onClick={() => adjustEdit(-1)} className="w-12 h-12 rounded-full bg-[#F4F6F5] flex items-center justify-center" data-testid="review-edit-minus">
                <Minus className="w-5 h-5 text-[#1D242B]" weight="bold" />
              </button>
              <input ref={inputRef} type="number" inputMode="numeric" value={editVal}
                onChange={e => setEditVal(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') commitEdit(); }}
                className="w-16 h-14 text-center text-2xl font-bold border-2 border-[#1B4332] rounded-xl bg-white text-[#1D242B]"
                data-testid="review-edit-input" />
              <button onClick={() => adjustEdit(1)} className="w-12 h-12 rounded-full bg-[#F4F6F5] flex items-center justify-center" data-testid="review-edit-plus">
                <Plus className="w-5 h-5 text-[#1D242B]" weight="bold" />
              </button>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1 h-10 rounded-xl text-sm" onClick={() => commitEdit()} data-testid="review-edit-save">Save</Button>
              {!isScramble && (
                <Button variant="outline" className="h-10 rounded-xl text-sm text-[#D32F2F] border-[#D32F2F]"
                  onClick={() => markNR(editingCell.playerId, editingCell.hole)} data-testid="review-edit-nr">NR</Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  /* ── Individual player grid (Stableford / NSP) ── */
  function PlayerGrid({ player, row, isFirst }) {
    const ph = getPlayerPH(player);
    const hp = getPlayerHolePars(player.id);
    const playerIsNR = isNR(player.id);

    return (
      <div className={!isFirst ? 'border-t-2 border-[#E1E6E4]' : ''}>
        <div className="px-3 py-2 bg-[#F4F6F5] flex items-center justify-between">
          <span className="text-xs font-bold text-[#1D242B] truncate">{player.name}</span>
          <span className="text-[10px] text-[#455561]">PH {formatPlayingHandicap(ph)}{playerIsNR ? ' · NR' : ''}</span>
        </div>
        {/* FRONT 9 */}
        <HalfGrid holeOffset={0} label="Out" showTot={false} hp={hp} row={row} pid={player.id} />
        {/* BACK 9 */}
        <HalfGrid holeOffset={9} label="In" showTot={true} hp={hp} row={row} pid={player.id} />
      </div>
    );
  }

  function HalfGrid({ holeOffset, label, showTot, hp, row, pid }) {
    const half = holeOffset < 9 ? row.out : row.inn;
    return (
      <div className={`grid ${GRID_COLS} text-[10px] border-t border-[#E1E6E4]`}>
        {/* Hole numbers header */}
        <div className="px-1 py-1 bg-[#1B4332] text-white font-semibold text-center">Hole</div>
        {Array.from({length:9},(_,i) => i + holeOffset).map(h => (
          <div key={h} className="px-0 py-1 bg-[#1B4332] text-white font-semibold text-center">{h+1}</div>
        ))}
        <div className="px-0 py-1 bg-[#163B2B] text-white font-bold text-center">{label}</div>
        <div className="px-0 py-1 bg-[#163B2B] text-white font-bold text-center">{showTot ? 'Tot' : ''}</div>

        {/* Par row */}
        <div className="px-1 py-0.5 text-[#455561] font-semibold">Par</div>
        {Array.from({length:9},(_,i) => i + holeOffset).map(h => (
          <div key={h} className="text-center text-[#455561]">{hp[h]||4}</div>
        ))}
        <div className="text-center font-bold text-[#455561] bg-[#F4F6F5]">{hp.slice(holeOffset, holeOffset+9).reduce((a,b)=>a+b,0)}</div>
        <div className="text-center font-bold text-[#455561] bg-[#F4F6F5]">{showTot ? hp.reduce((a,b)=>a+b,0) : ''}</div>

        {/* Gross score row — tappable, coloured */}
        <div className="px-1 py-0.5 font-semibold text-[#1D242B]">Gross</div>
        {Array.from({length:9},(_,i) => i + holeOffset).map(h => {
          const s = row.holes[h]?.gross || 0;
          const par = row.holes[h]?.par || 4;
          const hNR = isHoleNR(pid, h);
          return (
            <div key={h} className="flex items-center justify-center cursor-pointer hover:bg-[#E8E5DB] transition-colors"
              onClick={() => startEdit(pid, h)} data-testid={`review-cell-${pid}-${h}`}>
              {hNR
                ? <span className="inline-flex items-center justify-center w-6 h-6 rounded text-[9px] font-bold text-[#E59000] bg-[#FFF3E0]">NR</span>
                : s > 0
                  ? <span className={`inline-flex items-center justify-center w-6 h-6 rounded text-[9px] font-bold ${scoreColorClass(s, par)}`}>{s}</span>
                  : <span className="text-[#B8C7C0]">–</span>}
            </div>
          );
        })}
        <div className="text-center font-bold bg-[#F4F6F5]">{half.gross || ''}</div>
        <div className="text-center font-bold bg-[#E8E5DB]">{showTot ? row.total.gross : ''}</div>

        {/* Stableford points row */}
        {fmt === 'stableford' && <>
          <div className="px-1 py-0.5 font-semibold text-[#455561]">Pts</div>
          {Array.from({length:9},(_,i) => i + holeOffset).map(h => {
            const pts = row.holes[h]?.pts || 0;
            return <div key={h} className="text-center text-[#455561]">{pts}</div>;
          })}
          <div className="text-center font-bold text-[#455561] bg-[#F4F6F5]">{half.pts}</div>
          <div className="text-center font-bold text-[#455561] bg-[#E8E5DB]">{showTot ? row.total.pts : ''}</div>
        </>}

        {/* NSP net row */}
        {fmt === 'nsp' && <>
          <div className="px-1 py-0.5 font-semibold text-[#455561]">Net</div>
          {Array.from({length:9},(_,i) => i + holeOffset).map(h => (
            <div key={h} className="text-center text-[#455561]">{row.holes[h]?.net || 0}</div>
          ))}
          <div className="text-center font-bold text-[#455561] bg-[#F4F6F5]">{half.net}</div>
          <div className="text-center font-bold text-[#455561] bg-[#E8E5DB]">{showTot ? row.total.net : ''}</div>
        </>}
      </div>
    );
  }

  /* ── Scramble team grid ── */
  function ScrambleGrid({ row }) {
    return (
      <div>
        <div className="px-3 py-2 bg-[#F4F6F5]">
          <span className="text-xs font-bold text-[#1D242B]">Team {myGroupIdx + 1}</span>
          <span className="text-[10px] text-[#455561] ml-2">{myGroupPlayers.map(p => p.name).join(', ')}</span>
        </div>
        <ScrambleHalfGrid holeOffset={0} label="Out" showTot={false} row={row} />
        <ScrambleHalfGrid holeOffset={9} label="In" showTot={true} row={row} />
      </div>
    );
  }

  function ScrambleHalfGrid({ holeOffset, label, showTot, row }) {
    const half = holeOffset < 9 ? row.out : row.inn;
    return (
      <div className={`grid ${GRID_COLS} text-[10px] border-t border-[#E1E6E4]`}>
        <div className="px-1 py-1 bg-[#1B4332] text-white font-semibold text-center">Hole</div>
        {Array.from({length:9},(_,i) => i + holeOffset).map(h => (
          <div key={h} className="px-0 py-1 bg-[#1B4332] text-white font-semibold text-center">{h+1}</div>
        ))}
        <div className="px-0 py-1 bg-[#163B2B] text-white font-bold text-center">{label}</div>
        <div className="px-0 py-1 bg-[#163B2B] text-white font-bold text-center">{showTot ? 'Tot' : ''}</div>

        <div className="px-1 py-0.5 text-[#455561] font-semibold">Par</div>
        {Array.from({length:9},(_,i) => i + holeOffset).map(h => (
          <div key={h} className="text-center text-[#455561]">{holePars[h]||4}</div>
        ))}
        <div className="text-center font-bold text-[#455561] bg-[#F4F6F5]">{holePars.slice(holeOffset, holeOffset+9).reduce((a,b)=>a+b,0)}</div>
        <div className="text-center font-bold text-[#455561] bg-[#F4F6F5]">{showTot ? holePars.reduce((a,b)=>a+b,0) : ''}</div>

        <div className="px-1 py-0.5 font-semibold text-[#1D242B]">Gross</div>
        {Array.from({length:9},(_,i) => i + holeOffset).map(h => {
          const s = row.holes[h]?.gross || 0;
          const par = row.holes[h]?.par || 4;
          return (
            <div key={h} className="flex items-center justify-center cursor-pointer hover:bg-[#E8E5DB] transition-colors"
              onClick={() => startEdit(myGroupPids[0], h)} data-testid={`review-cell-team-${h}`}>
              {s > 0
                ? <span className={`inline-flex items-center justify-center w-6 h-6 rounded text-[9px] font-bold ${scoreColorClass(s, par)}`}>{s}</span>
                : <span className="text-[#B8C7C0]">–</span>}
            </div>
          );
        })}
        <div className="text-center font-bold bg-[#F4F6F5]">{half.gross || ''}</div>
        <div className="text-center font-bold bg-[#E8E5DB]">{showTot ? row.total.gross : ''}</div>
      </div>
    );
  }
}
